<script setup lang="ts">
import { markRaw, onMounted, ref, watch } from 'vue'
import {
  ArrowPathIcon,
  TruckIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Modal from '@/components/common/layout/Modal.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import { useDeliveryAdminStore, type AdminDeliveryRow } from '@/stores/useDeliveryAdminStore'

const store = useDeliveryAdminStore()

// ── Filtres ──────────────────────────────────────────────────────────────────
const search = ref('')
const filterState = ref('')
const filterPickingType = ref('')
const page = ref(1)
const pageSize = ref(50)

// Mois courant par défaut (format YYYY-MM)
function currentMonthValue() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}
const filterMonth = ref(currentMonthValue())

const STATE_OPTIONS = [
  { id: '', value: '', label: 'Tous les états' },
  { id: 'assigned', value: 'assigned', label: 'Prêt (assigned)' },
  { id: 'confirmed', value: 'confirmed', label: 'Confirmé' },
  { id: 'waiting', value: 'waiting', label: 'En attente' },
  { id: 'draft', value: 'draft', label: 'Brouillon' },
  { id: 'done', value: 'done', label: 'Terminé (done)' },
  { id: 'cancel', value: 'cancel', label: 'Annulé (cancel)' },
]

const TYPE_OPTIONS = [
  { id: '', value: '', label: 'PICK + OUT' },
  { id: 'PICK', value: 'PICK', label: 'PICK (préparation)' },
  { id: 'OUT', value: 'OUT', label: 'OUT (expédition)' },
]

// ── Sync ─────────────────────────────────────────────────────────────────────
const syncingOneUuid = ref<string | null>(null)
const syncNotif = ref<string | null>(null)

async function handleSyncAll() {
  const result = await store.syncAll()
  if (result) {
    syncNotif.value = `Sync terminée : ${result.synced} synchées, ${result.errors} erreurs`
    setTimeout(() => (syncNotif.value = null), 5000)
    await refresh()
  }
}

async function handleSyncOne(orderUuid: string) {
  syncingOneUuid.value = orderUuid
  await store.syncOne(orderUuid)
  syncingOneUuid.value = null
  await refresh()
}

// ── Édition ───────────────────────────────────────────────────────────────────
const showEditModal = ref(false)
const editRow = ref<AdminDeliveryRow | null>(null)
const editCarrierId = ref<number | null>(null)
const editTrackingRef = ref('')
const editError = ref<string | null>(null)

async function openEdit(row: AdminDeliveryRow) {
  editRow.value = row
  editCarrierId.value = null
  editTrackingRef.value = row.trackingRef ?? ''
  editError.value = null
  showEditModal.value = true
  if (store.carriers.length === 0) {
    await store.fetchCarriers(row.sourceCode)
  }
}

async function saveEdit() {
  if (!editRow.value) return
  editError.value = null
  const dto: { carrierId?: number; trackingRef?: string } = {}
  if (editCarrierId.value !== null) dto.carrierId = editCarrierId.value
  if (editTrackingRef.value !== editRow.value.trackingRef) dto.trackingRef = editTrackingRef.value
  if (!dto.carrierId && dto.trackingRef === undefined) {
    editError.value = 'Aucune modification à enregistrer'
    return
  }
  const ok = await store.updateDelivery(editRow.value.orderUuid, editRow.value.odooPickingId, dto)
  if (ok) {
    showEditModal.value = false
    await refresh()
  } else {
    editError.value = 'Erreur lors de la mise à jour'
  }
}

// ── Chargement ────────────────────────────────────────────────────────────────
async function refresh() {
  await store.fetchDeliveries({
    limit: pageSize.value,
    offset: (page.value - 1) * pageSize.value,
    state: filterState.value || undefined,
    pickingType: filterPickingType.value || undefined,
    search: search.value || undefined,
    month: filterMonth.value || undefined,
  })
}

let searchTimeout: ReturnType<typeof setTimeout> | undefined
function debouncedRefresh() {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(refresh, 400)
}

watch([filterState, filterPickingType, filterMonth, page], refresh)
watch(search, debouncedRefresh)

onMounted(refresh)

// ── Helpers d'affichage ───────────────────────────────────────────────────────
const STATE_CONFIG: Record<string, { label: string; class: string }> = {
  draft:     { label: 'Brouillon',   class: 'badge-ghost' },
  waiting:   { label: 'En attente',  class: 'badge-warning' },
  confirmed: { label: 'Confirmé',    class: 'badge-info' },
  assigned:  { label: 'Prêt',        class: 'badge-success' },
  done:      { label: 'Fait',        class: 'badge-neutral' },
  cancel:    { label: 'Annulé',      class: 'badge-error' },
}

function stateConfig(state: string) {
  return STATE_CONFIG[state] ?? { label: state, class: 'badge-ghost' }
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const arrowPathIcon = markRaw(ArrowPathIcon)
</script>

<template>
  <div class="p-6 space-y-6">

    <!-- Header -->
    <div class="flex items-center justify-between">
      <Bs2Title title="Gestion des Livraisons" :icon="TruckIcon" />
      <div class="flex items-center gap-2">
        <span v-if="syncNotif" class="text-sm text-success font-medium">{{ syncNotif }}</span>
        <ButtonDaisy
          text="Sync tout"
          variant="primary"
          :icon="arrowPathIcon"
          :loader="store.isSyncing"
          @click="handleSyncAll"
        />
      </div>
    </div>

    <!-- Filtres -->
    <div class="flex items-center gap-3 flex-wrap">
      <div class="relative">
        <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-base-content/40" />
        <input
          v-model="search"
          type="text"
          placeholder="Réf. commande, picking, transporteur..."
          class="input input-sm input-bordered pl-9 w-72"
        />
      </div>
      <SelectInput
        v-model="filterState"
        label="État"
        :options="STATE_OPTIONS"
        placeholder="État"
        class="w-48"
      />
      <SelectInput
        v-model="filterPickingType"
        label="Type"
        :options="TYPE_OPTIONS"
        placeholder="Type"
        class="w-40"
      />
      <!-- Filtre mois -->
      <div class="flex items-center gap-1">
        <input
          v-model="filterMonth"
          type="month"
          class="input input-sm input-bordered"
          title="Filtrer par mois (date prévue)"
        />
        <button
          v-if="filterMonth"
          class="btn btn-ghost btn-xs"
          title="Effacer le filtre de mois"
          @click="filterMonth = ''"
        >
          ✕
        </button>
      </div>

      <span class="text-sm text-base-content/50 ml-auto">{{ store.total }} résultat(s)</span>
    </div>

    <!-- Loading -->
    <div v-if="store.isLoading" class="flex justify-center py-12">
      <span class="loading loading-spinner loading-lg" />
    </div>

    <!-- Tableau -->
    <div v-else-if="store.deliveries.length" class="overflow-x-auto rounded-xl border border-base-300">
      <table class="table table-sm table-zebra w-full">
        <caption class="sr-only">Liste des livraisons en cours</caption>
        <thead>
          <tr>
            <th>Réf. commande</th>
            <th>Picking</th>
            <th>Type</th>
            <th>État</th>
            <th>Transporteur</th>
            <th>N° suivi</th>
            <th>Date prévue</th>
            <th>Dernière sync</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in store.deliveries" :key="row.uuid">
            <td class="font-mono text-xs">{{ row.quoteReference || '—' }}</td>
            <td class="font-mono text-xs">{{ row.odooPickingName }}</td>
            <td>
              <span :class="['badge badge-xs', row.pickingType === 'PICK' ? 'badge-warning' : 'badge-info']">
                {{ row.pickingType }}
              </span>
            </td>
            <td>
              <span :class="['badge badge-sm', stateConfig(row.state).class]">
                {{ stateConfig(row.state).label }}
              </span>
            </td>
            <td>{{ row.carrierName || '—' }}</td>
            <td class="font-mono text-xs">{{ row.trackingRef || '—' }}</td>
            <td class="text-xs">{{ formatDate(row.scheduledDate) }}</td>
            <td class="text-xs text-base-content/50">{{ formatDateTime(row.synchedAt) }}</td>
            <td class="text-right">
              <div class="flex items-center justify-end gap-1">
                <!-- Sync unitaire -->
                <button
                  class="btn btn-ghost btn-xs"
                  title="Forcer la synchronisation"
                  :disabled="syncingOneUuid === row.orderUuid"
                  @click="handleSyncOne(row.orderUuid)"
                >
                  <span v-if="syncingOneUuid === row.orderUuid" class="loading loading-spinner loading-xs" />
                  <ArrowPathIcon v-else class="size-4" />
                </button>
                <!-- Éditer (OUT uniquement) -->
                <button
                  v-if="row.pickingType === 'OUT'"
                  class="btn btn-ghost btn-xs"
                  title="Modifier transporteur / tracking"
                  @click="openEdit(row)"
                >
                  <PencilSquareIcon class="size-4" />
                </button>
                <span v-else class="w-[28px]" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div v-else class="flex flex-col items-center gap-3 py-16 text-base-content/40">
      <TruckIcon class="size-12" />
      <p>Aucune livraison active trouvée</p>
    </div>

    <!-- Pagination -->
    <div v-if="store.total > pageSize" class="flex justify-center gap-2">
      <button class="btn btn-sm btn-ghost" :disabled="page <= 1" @click="page--">←</button>
      <span class="btn btn-sm btn-ghost no-animation">Page {{ page }}</span>
      <button class="btn btn-sm btn-ghost" :disabled="page * pageSize >= store.total" @click="page++">→</button>
    </div>

    <!-- Modal d'édition -->
    <Modal
      :show-modal="showEditModal"
      :title="`Modifier — ${editRow?.odooPickingName ?? ''}`"
      :loading="store.isSavingEdit"
      max-width="md"
      @close="showEditModal = false"
    >
      <template #content>
        <div class="space-y-4 mt-4">

          <!-- Infos lecture seule -->
          <div class="grid grid-cols-2 gap-2 text-sm bg-base-200 rounded-lg p-3">
            <div>
              <span class="text-base-content/50">Commande</span>
              <p class="font-mono font-medium">{{ editRow?.quoteReference }}</p>
            </div>
            <div>
              <span class="text-base-content/50">État actuel</span>
              <p>
                <span :class="['badge badge-sm', stateConfig(editRow?.state ?? '').class]">
                  {{ stateConfig(editRow?.state ?? '').label }}
                </span>
              </p>
            </div>
          </div>

          <!-- Transporteur -->
          <div>
            <label class="label"><span class="label-text font-medium">Transporteur</span></label>
            <select v-model="editCarrierId" class="select select-sm select-bordered w-full">
              <option :value="null">— Conserver l'actuel ({{ editRow?.carrierName || 'non défini' }}) —</option>
              <option v-for="c in store.carriers" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
            <p v-if="store.carriers.length === 0" class="text-xs text-base-content/40 mt-1">
              Chargement des transporteurs...
            </p>
          </div>

          <!-- Tracking ref -->
          <div>
            <label class="label"><span class="label-text font-medium">Numéro de suivi</span></label>
            <input
              v-model="editTrackingRef"
              type="text"
              placeholder="Ex: 1Z999AA10123456784"
              class="input input-sm input-bordered w-full font-mono"
            />
          </div>

          <!-- Erreur -->
          <p v-if="editError" class="text-sm text-error">{{ editError }}</p>

          <!-- Boutons -->
          <div class="flex justify-end gap-2 pt-2">
            <ButtonDaisy text="Annuler" variant="ghost" @click="showEditModal = false" />
            <ButtonDaisy
              text="Enregistrer"
              variant="primary"
              :loader="store.isSavingEdit"
              @click="saveEdit"
            />
          </div>
        </div>
      </template>
    </Modal>

  </div>
</template>
